import React, { useEffect, useRef, useState } from "react";
import { Modal, ModalDialog, Button, Input, Select, Option, CircularProgress } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getFetch, postFetch } from "../../../../../../fetch/fetch";
import styles from "./staffmanage.module.css";
import { renderMappingChip } from "./parttable";
import {
    CONTRACT_VAR_KEYS, NONE_KEY, PART_VAR_FIELDS, PartVars, emptyPartVars, TemplateField, computeMappingStatus
} from "./contractvarkeys";

//파트 편집/추가 다이얼로그
//왼쪽: 파트명 · 유캔싸인 탬플릿(실시간 목록) · 파트 변수 8개
//오른쪽: 선택한 탬플릿의 빈칸(requesterInputs) 마다 어떤 값을 넣을지 Select. 연결 안 된 빈칸은 노란 배경
//탬플릿을 바꾸면 매핑은 서버 자동 제안으로 초기화. 저장 시 fieldMapping 은 탬플릿의 모든 fieldId 를 키로 (미연결 = null)
const API = "/dashboard/employeedirectory";
const toastOptions: any = { position: "bottom-right", autoClose: 3000, hideProgressBar: true, theme: "colored" };

interface TemplateItem {
    templateId: string;
    name: string;
    updatedAt?: string;
}

const PartEditModal: React.FC<any> = (props) => {

    const [name, setName] = useState("");
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [templateName, setTemplateName] = useState<string>("");
    const [vars, setVars] = useState<PartVars>(emptyPartVars());

    const [templates, setTemplates] = useState<TemplateItem[]>([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [templatesError, setTemplatesError] = useState(false);

    const [fields, setFields] = useState<TemplateField[]>([]);
    const [fieldsLoading, setFieldsLoading] = useState(false);
    const [mapping, setMapping] = useState<{ [fieldId: string]: string | null }>({});

    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    //탬플릿을 빠르게 바꿀 때 늦게 도착한 응답 무시용
    const fieldsReqSeq = useRef(0);

    useEffect(() => {
        if (!props.open) {
            return;
        }
        const part = props.part;
        setName(part ? part.name : "");
        setTemplateId(part && part.templateId ? String(part.templateId) : null);
        setTemplateName(part && part.templateName ? part.templateName : "");
        const nextVars = emptyPartVars();
        if (part && part.vars) {
            PART_VAR_FIELDS.forEach((f) => { nextVars[f.key] = String(part.vars[f.key] || ""); });
        }
        setVars(nextVars);
        setFields([]);
        setMapping({});
        setSaving(false);
        setDeleting(false);
        fieldsReqSeq.current += 1;

        loadTemplates();
        if (part && part.templateId) {
            loadFields(String(part.templateId), true);
        }
    }, [props.open]);

    const loadTemplates = async () => {
        try {
            setTemplatesLoading(true);
            setTemplatesError(false);
            const result: any = await getFetch(`${API}/ucansign/templates`);
            if (result.message === "success") {
                setTemplates(result.templates || []);
            } else {
                setTemplatesError(true);
                toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다 (todoistApiKey 필요)" : `유캔싸인 탬플릿 목록을 불러오지 못했습니다${result.detail ? " (" + result.detail + ")" : ""}`, toastOptions);
            }
        } catch (e) {
            console.log(e);
            setTemplatesError(true);
            toast.error("유캔싸인 탬플릿 목록을 불러오지 못했습니다", toastOptions);
        } finally {
            setTemplatesLoading(false);
        }
    };

    //keepExisting: 기존 파트를 다시 열 때는 저장된 매핑 우선, 탬플릿에 새로 생긴 빈칸만 자동 제안
    const loadFields = async (id: string, keepExisting: boolean) => {
        const seq = ++fieldsReqSeq.current;
        setFieldsLoading(true);
        setFields([]);
        try {
            const result: any = await getFetch(`${API}/ucansign/templates/${id}`);
            if (seq !== fieldsReqSeq.current) {
                return;
            }
            if (result.message !== "success") {
                toast.error(`탬플릿 빈칸을 불러오지 못했습니다${result.detail ? " (" + result.detail + ")" : ""}`, toastOptions);
                setFieldsLoading(false);
                return;
            }
            const nextFields: TemplateField[] = result.fields || [];
            const saved = keepExisting && props.part && props.part.fieldMapping ? props.part.fieldMapping : null;
            const next: { [fieldId: string]: string | null } = {};
            nextFields.forEach((f) => {
                if (saved && Object.prototype.hasOwnProperty.call(saved, f.fieldId)) {
                    next[f.fieldId] = saved[f.fieldId] || null;
                } else {
                    next[f.fieldId] = result.suggested && result.suggested[f.fieldId] ? result.suggested[f.fieldId] : null;
                }
            });
            if (result.templateName && !keepExisting) {
                setTemplateName(result.templateName);
            }
            setFields(nextFields);
            setMapping(next);
            setFieldsLoading(false);
        } catch (e) {
            console.log(e);
            if (seq === fieldsReqSeq.current) {
                toast.error("탬플릿 빈칸을 불러오지 못했습니다", toastOptions);
                setFieldsLoading(false);
            }
        }
    };

    const changeTemplate = (v: any) => {
        if (v === null) {
            return;
        }
        if (v === NONE_KEY) {
            fieldsReqSeq.current += 1;
            setTemplateId(null);
            setTemplateName("");
            setFields([]);
            setMapping({});
            setFieldsLoading(false);
            return;
        }
        const found = templates.find((t) => t.templateId === v);
        setTemplateId(String(v));
        setTemplateName(found ? found.name : templateName);
        loadFields(String(v), false);
    };

    const save = async () => {
        if (!name.trim()) {
            toast.error("파트명을 입력하세요", toastOptions);
            return;
        }
        try {
            setSaving(true);
            const result: any = await postFetch(`${API}/parts`, {
                part: {
                    id: props.part ? props.part.id : undefined,
                    name: name.trim(),
                    templateId,
                    templateName: templateId ? templateName : "",
                    vars,
                    fieldMapping: templateId ? mapping : {},
                    templateFields: templateId ? fields : [],
                    sortOrder: props.part ? props.part.sortOrder : props.nextSortOrder
                }
            });
            if (result.message === "success") {
                toast.success("저장되었습니다", toastOptions);
                props.onSaved();
                props.onClose();
            } else if (result.message === "duplicateName") {
                toast.error("같은 이름의 파트가 이미 있습니다", toastOptions);
            } else {
                toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다 (todoistApiKey 필요)" : `저장에 실패했습니다 (${result.detail || result.message})`, toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!props.part) {
            return;
        }
        if (!window.confirm(`${props.part.name} 파트를 삭제할까요? (이 파트를 쓰는 직무가 있으면 계약서를 보낼 수 없게 됩니다)`)) {
            return;
        }
        try {
            setDeleting(true);
            const result: any = await postFetch(`${API}/parts/delete`, { id: props.part.id });
            if (result.message === "success") {
                toast.success("삭제되었습니다", toastOptions);
                props.onSaved();
                props.onClose();
            } else {
                toast.error(result.message === "NOT_AUTHORIZED" ? "권한이 없습니다 (todoistApiKey 필요)" : `삭제에 실패했습니다 (${result.detail || result.message})`, toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("오류가 발생했습니다", toastOptions);
        } finally {
            setDeleting(false);
        }
    };

    //저장된 탬플릿이 실시간 목록에 없으면(유캔싸인에서 삭제됨) 회색 폴백 옵션으로 표시
    const templateInList = templateId ? templates.some((t) => t.templateId === templateId) : true;
    const status = computeMappingStatus(templateId, fields, mapping);

    return (
        <Modal open={props.open} onClose={props.onClose}>
            <ModalDialog sx={{ width: "1100px", maxWidth: "95vw", maxHeight: "90vh", overflow: "auto" }}>

                <div className={styles.modalTitle}>{props.part ? "파트 편집" : "파트 추가"}</div>

                <div style={{ display: "flex", gap: "28px" }}>

                    {/* 왼쪽: 파트명 · 탬플릿 · 변수 8개 */}
                    <div style={{ width: "380px", flexShrink: 0 }}>
                        <div className={styles.fieldLabel}>파트명</div>
                        <Input size="sm" value={name} onChange={(e) => setName(e.target.value)} />

                        <div className={styles.fieldLabel} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>유캔싸인 탬플릿 {templatesLoading ? "(불러오는 중…)" : "(유캔싸인에서 목록 불러옴)"}</span>
                            {templatesError && <Button size="sm" variant="plain" onClick={loadTemplates}>다시 시도</Button>}
                        </div>
                        <Select
                            size="sm"
                            value={templateId || NONE_KEY}
                            onChange={(e: any, v: any) => changeTemplate(v)}
                            disabled={templatesLoading}
                        >
                            <Option value={NONE_KEY}>(선택 안 함)</Option>
                            {!templateInList && templateId &&
                                <Option value={templateId} sx={{ color: "#9ca3af" }}>목록에 없는 탬플릿 · {templateName || templateId}</Option>
                            }
                            {templates.map((t) => (
                                <Option key={t.templateId} value={t.templateId}>{t.name}</Option>
                            ))}
                        </Select>

                        {PART_VAR_FIELDS.map((f) => (
                            <div key={f.key}>
                                <div className={styles.fieldLabel}>{f.label}</div>
                                <Input
                                    size="sm"
                                    value={vars[f.key]}
                                    onChange={(e) => { const v = e.target.value; setVars((prev) => ({ ...prev, [f.key]: v })); }}
                                />
                            </div>
                        ))}
                    </div>

                    {/* 오른쪽: 빈칸 ↔ 변수 연결 */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div className={styles.fieldLabel} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>탬플릿 빈칸 ↔ 우리 변수 연결 (탬플릿 고르면 빈칸 목록이 자동으로 나옴)</span>
                            {renderMappingChip(status)}
                        </div>

                        {!templateId &&
                            <div style={{ padding: "40px 0", textAlign: "center", fontFamily: "Apple_R", color: "#9ca3af", background: "#f9fafb", borderRadius: "8px" }}>
                                탬플릿을 고르면 빈칸 목록이 자동으로 나옵니다
                            </div>
                        }

                        {templateId && fieldsLoading &&
                            <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                                <CircularProgress size="sm" />
                            </div>
                        }

                        {templateId && !fieldsLoading &&
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: "8px", overflow: "hidden" }}>
                                <div style={{ display: "flex", alignItems: "center", padding: "8px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb", fontSize: "12px", color: "#888", gap: "12px" }}>
                                    <div style={{ width: "240px", flexShrink: 0 }}>탬플릿의 빈칸</div>
                                    <div style={{ flex: 1 }}>여기 들어갈 값</div>
                                </div>
                                {fields.filter((f) => f.fieldType !== "signature").map((f) => {
                                    const unmapped = !mapping[f.fieldId];
                                    return (
                                        <div key={f.fieldId} style={{ display: "flex", alignItems: "center", gap: "12px", padding: "6px 8px", borderBottom: "1px solid #f0f0f0", backgroundColor: unmapped ? "#fff8e1" : "transparent" }}>
                                            <div style={{ width: "240px", flexShrink: 0, fontSize: "13px", fontFamily: "Apple_R" }}>
                                                {f.fieldName}{f.required ? <span style={{ color: "#d32f2f", marginLeft: "4px" }}>*</span> : null}
                                                {/* 같은 이름이 두 번 나오는 탬플릿 대비 fieldId 노출 */}
                                                <div style={{ fontSize: "10px", color: "#9ca3af" }}>{f.fieldId}</div>
                                            </div>
                                            <Select
                                                size="sm"
                                                sx={{ flex: 1, minWidth: 0 }}
                                                value={mapping[f.fieldId] || NONE_KEY}
                                                onChange={(e: any, v: any) => {
                                                    if (v !== null) {
                                                        setMapping((prev) => ({ ...prev, [f.fieldId]: v === NONE_KEY ? null : v }));
                                                    }
                                                }}
                                            >
                                                <Option value={NONE_KEY}>(연결 안 함)</Option>
                                                {CONTRACT_VAR_KEYS.map((k) => (
                                                    <Option key={k.value} value={k.value}>{k.label}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                    );
                                })}
                                {fields.filter((f) => f.fieldType !== "signature").length === 0 &&
                                    <div style={{ padding: "20px", fontFamily: "Apple_R", color: "#9ca3af", fontSize: "13px" }}>이 탬플릿에는 요청자가 채울 빈칸이 없습니다</div>
                                }
                            </div>
                        }
                    </div>
                </div>

                <div className={styles.modalButtons}>
                    <div>
                        {props.part &&
                            <Button variant="plain" color="danger" onClick={remove} loading={deleting} disabled={saving}>삭제</Button>
                        }
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Button variant="plain" color="neutral" onClick={props.onClose} disabled={saving || deleting}>닫기</Button>
                        <Button variant="solid" onClick={save} loading={saving} disabled={fieldsLoading || deleting}>저장</Button>
                    </div>
                </div>

            </ModalDialog>
        </Modal>
    );
};

export default PartEditModal;
